import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Calendar as CalendarIcon, Upload, Settings } from "lucide-react";
import ShiftCalendar from "../components/attendance/ShiftCalendar";
import AttendanceUpload from "../components/attendance/AttendanceUpload";
import HolidayList from "../components/attendance/HolidayList";
import AttendanceList from "../components/attendance/AttendanceList";
import { ShiftManager } from "../components/attendance/ShiftManager";
import { WeeklyPlanner } from "../components/attendance/WeeklyPlanner";
import { AbsenceManager } from "../components/attendance/AbsenceManager";
import { PerformanceDashboard } from "../components/attendance/PerformanceDashboard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LayoutGrid, Plane, BarChart3 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const AttendancePage = () => {
  const { can } = useAuth();
  const [activeTab, setActiveTab] = useState("calendar");

  const canReadAttendance = can("attendance.read");
  const canSetupAttendance = can("attendance.setup");
  const canUploadAttendance = can("attendance.upload");

  const availableTabs = useMemo(
    () =>
      [
        canReadAttendance && "calendar",
        canSetupAttendance && "planner",
        canReadAttendance && "absences",
        canReadAttendance && "performance",
        canUploadAttendance && "upload",
        canReadAttendance && "records",
        canSetupAttendance && "settings",
      ].filter(Boolean) as string[],
    [canReadAttendance, canSetupAttendance, canUploadAttendance],
  );

  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [activeTab, availableTabs]);

  if (!canReadAttendance) {
    return (
      <Card className="border-slate-200/70 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-900">Acceso restringido</CardTitle>
          <CardDescription>
            Esta vista de asistencia y desempeño solo está disponible para roles con permiso de lectura.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900">
              Control de Asistencia y Horarios
            </h1>
          </div>
          <p className="text-slate-600">
            Gestiona los turnos, días festivos y registros de asistencia de tu personal.
          </p>
        </div>
      </div>

      <TooltipProvider>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1 border border-slate-200/60 w-fit rounded-xl gap-1">
            {canReadAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="calendar" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <CalendarIcon className="h-4 w-4" />
                  Calendario y Turnos
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Planificación de horarios y festivos</p>
              </TooltipContent>
            </Tooltip>
            )}

            {canSetupAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="planner" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <LayoutGrid className="h-4 w-4" />
                  Planificador Semanal
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Asignación dinámica de turnos</p>
              </TooltipContent>
            </Tooltip>
            )}

            {canReadAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="absences" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <Plane className="h-4 w-4" />
                  Ausencias
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Gestión de vacaciones y permisos</p>
              </TooltipContent>
            </Tooltip>
            )}

            {canReadAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="performance" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <BarChart3 className="h-4 w-4" />
                  Métricas
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Desempeño y Calidad</p>
              </TooltipContent>
            </Tooltip>
            )}

            {canUploadAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="upload" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <Upload className="h-4 w-4" />
                  Cargar Asistencia
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Subir registros mediante Excel</p>
              </TooltipContent>
            </Tooltip>
            )}

            {canReadAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="records" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <Clock className="h-4 w-4" />
                  Registros
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Historial de entradas y salidas</p>
              </TooltipContent>
            </Tooltip>
            )}

            {canSetupAttendance && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="settings" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <Settings className="h-4 w-4" />
                  Configuración
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Ajustes globales de asistencia</p>
              </TooltipContent>
            </Tooltip>
            )}
          </TabsList>

          {canReadAttendance && (
          <TabsContent value="calendar" className="animate-in fade-in-50 duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ShiftCalendar />
              </div>
              <div className="space-y-6">
                <HolidayList />
              </div>
            </div>
          </TabsContent>
          )}

          {canSetupAttendance && (
          <TabsContent value="planner" className="animate-in fade-in-50 duration-500">
            <WeeklyPlanner />
          </TabsContent>
          )}

          {canReadAttendance && (
          <TabsContent value="absences" className="animate-in fade-in-50 duration-500">
            <AbsenceManager />
          </TabsContent>
          )}

          {canReadAttendance && (
          <TabsContent value="performance" className="animate-in fade-in-50 duration-500">
            <PerformanceDashboard />
          </TabsContent>
          )}

          {canUploadAttendance && (
          <TabsContent value="upload" className="animate-in fade-in-50 duration-500">
            <AttendanceUpload />
          </TabsContent>
          )}

          {canReadAttendance && (
          <TabsContent value="records" className="animate-in fade-in-50 duration-500">
            <AttendanceList />
          </TabsContent>
          )}

          {canSetupAttendance && (
          <TabsContent value="settings" className="animate-in fade-in-50 duration-500">
            <ShiftManager />
          </TabsContent>
          )}
        </Tabs>
      </TooltipProvider>
    </div>
  );
};

export default AttendancePage;
